import { forwardRef, ElementRef, ForwardedRef, useState } from "react";
import "react-dates/lib/css/_datepicker.css";
import "react-dates/initialize";

import {
  SingleDatePicker as ReactDatesSingleDatePicker,
  SingleDatePickerShape,
} from "react-dates";
import {
  BaseDatePicker,
  Caption,
  createIcon,
  StyledDatePickerContainer,
} from "../common";
import { MonthYearSelector } from "./MonthYearSelector";
import { Icon } from "../Icon";
import { FlattenSimpleInterpolation } from "styled-components";

type DatePickerProps = SingleDatePickerShape & {
  caption?: string;
  errorMessage?: string;
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
  showMonthYearSelector?: boolean;
  styles?: FlattenSimpleInterpolation;
};

export const SingleDatePicker = forwardRef(
  (
    props: DatePickerProps,
    ref: ForwardedRef<ElementRef<typeof ReactDatesSingleDatePicker>>
  ) => {
    const [focused, setFocused] = useState(props.focused);
    const [date, setDate] = useState(props.date);

    return (
      <BaseDatePicker
        disabled={props.disabled}
        errorMessage={props.errorMessage}
        isRequired={props.isRequired}
        label={props.label}
        id={props.id}
      >
        <StyledDatePickerContainer
          disabled={props.disabled}
          readOnly={props.readOnly}
          hasError={!!props.errorMessage}
          hasSelectors={!!props.showMonthYearSelector}
          styles={props.styles}
        >
          <ReactDatesSingleDatePicker
            {...props}
            ref={ref}
            disabled={props.disabled || props.readOnly}
            required={props.isRequired}
            isOutsideRange={props.isOutsideRange ?? (() => false)}
            numberOfMonths={props.numberOfMonths ?? 1}
            daySize={props.daySize ?? 32}
            navPrev={
              <Icon
                iconSize="small"
                className="nav-prev"
                iconName="chevron-left"
              />
            }
            navNext={
              <Icon
                iconSize="small"
                className="nav-next"
                iconName="chevron-right"
              />
            }
            hideKeyboardShortcutsPanel={
              props.hideKeyboardShortcutsPanel ?? true
            }
            customInputIcon={
              props.customInputIcon ??
              createIcon(
                props.disabled ?? false,
                props.readOnly ?? false,
                focused ?? false
              )
            }
            inputIconPosition={props.inputIconPosition ?? "after"}
            block
            noBorder
            small
            date={date}
            focused={focused}
            onDateChange={(newDate) => {
              setDate(newDate);
              props.onDateChange && props.onDateChange(newDate);
            }}
            onFocusChange={(focused) => setFocused(focused.focused)}
            {...(props.showMonthYearSelector && {
              renderMonthElement: MonthYearSelector,
            })}
            renderMonthText={props.renderMonthText as any} // there's a bug in @types/react-dates
          />
        </StyledDatePickerContainer>
        {props.caption && (
          <Caption
            isDisabled={props.disabled || props.readOnly}
            hasError={!!props.errorMessage}
          >
            {props.errorMessage || props.caption}
          </Caption>
        )}
      </BaseDatePicker>
    );
  }
);

SingleDatePicker.displayName = "SingleDatePicker";
